import { InjectionToken, isDevMode } from '@angular/core';

import { SbSDKOptions, storyblokInit } from '@storyblok/js';
import StoryblokClient from 'storyblok-js-client';

// Timeout the server fetch after 8 seconds so it will not block the server indefinitely if Storyblok is not responding. The client will retry the request after 1 second, so this should be enough time for the server to recover and respond to the next request.
const SB_SERVER_FETCH_TIMEOUT_MS = 8000;

export function fetchWithCookiesEnabled(
	input: string | URL | globalThis.Request,
	init?: RequestInit,
) {
	if (isDevMode()) {
		if (init) {
			init.credentials = 'include';
		}
	}

	// Apply the timeout only on the server; in the browser the SDK manages its
	// own request lifecycle and we don't want to change client behavior.
	/* v8 ignore start */
	if (globalThis.window === undefined) {
		init = {
			...init,
			signal:
				init?.signal ?? AbortSignal.timeout(SB_SERVER_FETCH_TIMEOUT_MS),
		};
	}
	/* v8 ignore stop */

	return fetch(input, init);
}

export const SB_CLIENT_FACTORY = new InjectionToken<
	typeof initializeStoryblokClient
>('storyblok-client-factory');

export function initializeStoryblokClient(
	accessToken: string,
	endpoint: string | null,
): StoryblokClient | undefined {
	return storyblokInit({
		accessToken,
		use: [
			// This is a modified ApiFactory, just to be able to alter the endpoint parameter in the
			// StoryblokClient
			(options: SbSDKOptions) => {
				const { apiOptions } = options;
				/* v8 ignore start */
				if (!apiOptions?.accessToken) {
					console.error(
						'You need to provide an access token to interact with Storyblok API. Read https://www.storyblok.com/docs/api/content-delivery#topics/authentication',
					);
					return {};
				}
				/* v8 ignore stop */
				const storyblokApi = new StoryblokClient(
					{
						...apiOptions,
						cache: {
							clear: 'auto',
							type: 'memory',
						},
						fetch: fetchWithCookiesEnabled,
					},
					endpoint ?? '',
				);
				return { storyblokApi };
			},
		],
	}).storyblokApi;
}
